import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { motion } from 'framer-motion';
import { BookOpen, GraduationCap, Users, ChevronRight, Eye, EyeOff, Zap } from 'lucide-react';

export default function AuthPage({ onNavigateToTrialStarted }) {
  const { login, register, refreshUser } = useAuth();
  const reactNavigate = useNavigate();
  const [isLogin, setIsLogin] = useState(true);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState('student');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const roles = [
    { id: 'student', label: 'Student', icon: GraduationCap, desc: 'I want to learn & practice' },
    { id: 'parent', label: 'Parent', icon: Users, desc: 'I want to track my child' },
    { id: 'teacher', label: 'Teacher', icon: BookOpen, desc: 'I set chapter progress' },
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      if (isLogin) {
        await login(email, password);
        // Facebook Pixel: Track Login
        if (window.fbq) window.fbq('track', 'Login');
      } else {
        // Register the user
        await register(name, email, password, role);
        
        // Facebook Pixel: Track SignUp/Lead
        if (window.fbq) {
          window.fbq('track', 'Lead', { content_name: role });
          window.fbq('track', 'CompleteRegistration', { content_name: role });
        }
        
        // Calculate trial end date
        const trialEndDate = new Date();
        trialEndDate.setDate(trialEndDate.getDate() + 14);
        
        // Navigate to /trial-started BEFORE refreshUser()
        // This prevents the subscription gate from redirecting
        console.log('Signup successful, navigating to /trial-started');
        
        if (onNavigateToTrialStarted) {
          onNavigateToTrialStarted({
            name: name,
            email: email,
            role: role,
            plan: 'trial',
            trialEndsAt: trialEndDate.toISOString()
          });
        } else {
          // Fallback to direct navigation
          reactNavigate('/trial-started', {
            state: {
              name: name,
              email: email,
              role: role,
              plan: 'trial',
              trialEndsAt: trialEndDate.toISOString()
            }
          });
        }
        
        // Delay refreshUser to allow navigation to complete
        setTimeout(() => {
          refreshUser();
        }, 500);
        
        setLoading(false);
        return; // Exit early, don't continue to normal flow
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Something went wrong');
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-[#0f172a] flex flex-col items-center justify-center px-4 relative overflow-hidden">
      {/* Background glow */}
      <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[600px] h-[600px] rounded-full opacity-20" 
           style={{background: 'radial-gradient(circle, rgba(124,58,237,0.3) 0%, transparent 70%)'}} />
      
      <motion.div 
        initial={{ opacity: 0, y: 30 }} 
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md relative z-10"
      >
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 mb-4">
            <div className="w-10 h-10 rounded-xl bg-violet-600 flex items-center justify-center glow-violet">
              <Zap className="w-6 h-6 text-white" />
            </div>
            <h1 className="text-3xl font-bold text-white font-['Outfit']" data-testid="app-logo">StudyClaw</h1>
          </div>
          <p className="text-slate-400 text-sm">AI-powered exam coach synced with your school</p>
        </div>

        {/* Auth Card */}
        <div className="bg-slate-900/60 border border-slate-800 rounded-2xl p-6 backdrop-blur-sm">
          {/* Tabs */}
          <div className="flex gap-1 bg-slate-950/50 rounded-xl p-1 mb-6">
            <button
              data-testid="login-tab"
              onClick={() => setIsLogin(true)}
              className={`flex-1 py-2.5 rounded-lg text-sm font-medium transition-all ${isLogin ? 'bg-violet-600 text-white shadow-lg' : 'text-slate-400 hover:text-white'}`}
            >
              Log In
            </button>
            <button
              data-testid="signup-tab"
              onClick={() => setIsLogin(false)}
              className={`flex-1 py-2.5 rounded-lg text-sm font-medium transition-all ${!isLogin ? 'bg-violet-600 text-white shadow-lg' : 'text-slate-400 hover:text-white'}`}
            >
              Sign Up
            </button>
          </div>

          {/* Role Selection (signup only) */}
          {!isLogin && (
            <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} className="mb-5">
              <label className="text-xs uppercase tracking-widest text-slate-500 mb-2 block">I am a</label>
              <div className="grid grid-cols-3 gap-2">
                {roles.map(r => (
                  <button
                    key={r.id}
                    data-testid={`role-${r.id}`}
                    onClick={() => setRole(r.id)}
                    className={`p-3 rounded-xl border text-center transition-all ${
                      role === r.id 
                        ? 'border-violet-500 bg-violet-600/10 text-white' 
                        : 'border-slate-800 bg-slate-950/30 text-slate-400 hover:border-slate-700'
                    }`}
                  >
                    <r.icon className={`w-5 h-5 mx-auto mb-1 ${role === r.id ? 'text-violet-400' : ''}`} />
                    <div className="text-xs font-medium">{r.label}</div>
                  </button>
                ))}
              </div>
            </motion.div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            {!isLogin && (
              <div>
                <label className="text-xs uppercase tracking-widest text-slate-500 mb-1.5 block">Full Name</label>
                <input
                  data-testid="name-input"
                  type="text"
                  value={name}
                  onChange={e => setName(e.target.value)}
                  placeholder="Enter your name"
                  required={!isLogin}
                  className="w-full bg-slate-950/50 border border-slate-800 focus:border-violet-500 focus:ring-1 focus:ring-violet-500/20 rounded-xl h-12 px-4 text-slate-100 placeholder:text-slate-600 outline-none transition-all"
                />
              </div>
            )}

            <div>
              <label className="text-xs uppercase tracking-widest text-slate-500 mb-1.5 block">Email</label>
              <input
                data-testid="email-input"
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="you@email.com"
                required
                className="w-full bg-slate-950/50 border border-slate-800 focus:border-violet-500 focus:ring-1 focus:ring-violet-500/20 rounded-xl h-12 px-4 text-slate-100 placeholder:text-slate-600 outline-none transition-all"
              />
            </div>

            <div className="relative">
              <label className="text-xs uppercase tracking-widest text-slate-500 mb-1.5 block">Password</label>
              <input
                data-testid="password-input"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={e => setPassword(e.target.value)}
                placeholder="Enter password"
                required
                className="w-full bg-slate-950/50 border border-slate-800 focus:border-violet-500 focus:ring-1 focus:ring-violet-500/20 rounded-xl h-12 px-4 pr-11 text-slate-100 placeholder:text-slate-600 outline-none transition-all"
              />
              <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-[38px] text-slate-500 hover:text-slate-300">
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>

            {error && (
              <div data-testid="auth-error" className="text-red-400 text-sm bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2">
                {error}
              </div>
            )}

            <button
              data-testid="auth-submit-btn"
              type="submit"
              disabled={loading}
              className="w-full bg-violet-600 hover:bg-violet-700 text-white rounded-xl h-12 font-semibold text-sm transition-all glow-violet flex items-center justify-center gap-2 disabled:opacity-50"
            >
              {loading ? (
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : (
                <>
                  {isLogin ? 'Log In' : 'Create Account'}
                  <ChevronRight className="w-4 h-4" />
                </>
              )}
            </button>
          </form>
        </div>

        {/* Demo accounts hint */}
        <p className="text-center text-slate-600 text-xs mt-4">
          StudyClaw v4.2 - AI Exam Coach for CBSE
        </p>
      </motion.div>
    </div>
  );
}
